import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Save, Send, Eye, WifiOff, RefreshCcw, AlertCircle, Trash2 } from 'lucide-react';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import Loading from '../../components/Loading/Loading';
import { shouldAutoSaveDocumentForm } from '../../components/documentFormPersistenceUtils';
import {
  buildQueueItem,
  hasConflict,
  readQueue,
  removeQueueItem,
  summarizeQueue,
  updateQueueItemStatus,
  upsertQueueItem,
  writeQueue,
} from './offlineSyncQueue';

const DRAFT_KEY_PREFIX = 'audit_form_draft_';
const SYNC_INTERVAL_MS = 30000;
const AuditFormEntry = () => {
  const [projectId, setProjectId] = useState('');
  const [scopeKey, setScopeKey] = useState('');
  const [documentCode, setDocumentCode] = useState('');
  const [formData, setFormData] = useState({});
  const [catalogItem, setCatalogItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [localVersion, setLocalVersion] = useState(1);
  const [syncStatus, setSyncStatus] = useState('DRAFT');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingSync, setPendingSync] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [syncQueue, setSyncQueue] = useState(() => readQueue());
  const syncIntervalRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const lastAutoSavedJsonRef = useRef('');
  const queueSummary = summarizeQueue(syncQueue);
  const autoSaveEnabled = shouldAutoSaveDocumentForm(documentCode);

  const persistQueue = useCallback((queue) => {
    setSyncQueue(queue);
    writeQueue(queue);
    setPendingSync(summarizeQueue(queue).total > 0);
  }, []);

  const queueDraftForSync = useCallback((pId, sKey, docCode, data, version, patch = {}) => {
    const queueItem = buildQueueItem({
      projectId: pId,
      scopeKey: sKey,
      documentCode: docCode,
      formData: data,
      localVersion: version,
      ...patch,
    });
    persistQueue(upsertQueueItem(readQueue(), queueItem));
  }, [persistQueue]);

  useEffect(() => {
    const handleOnline = () => { setIsOffline(false); setPendingSync(readQueue().length > 0); };
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const queue = readQueue();
    setSyncQueue(queue);
    setPendingSync(queue.length > 0);
  }, []);

  const loadDraftFromServer = useCallback(async (pId, sKey, docCode) => {
    if (!pId || !sKey || !docCode) return;
    setLoading(true);
    try {
      const res = await api.get(`/audit/form-drafts/project/${pId}/scope/${sKey}/doc/${docCode}`);
      if (res.data?.found) {
        const serverFormData = res.data.formData || {};
        setDraftId(res.data.id);
        setFormData(serverFormData);
        setSyncStatus(res.data.syncStatus || 'DRAFT');
        setLocalVersion(res.data.version || 1);
        lastAutoSavedJsonRef.current = JSON.stringify(serverFormData);
        setAutoSaveStatus('saved');
      }
    } catch (error) {
      console.debug('No server draft found');
    } finally {
      setLoading(false);
    }
  }, []);


  const loadDraftFromLocal = useCallback((pId, sKey, docCode) => {
    const key = `${DRAFT_KEY_PREFIX}${pId}_${sKey}_${docCode}`;
    const local = localStorage.getItem(key);
    if (local) {
      try {
        const parsed = JSON.parse(local);
        const localFormData = parsed.formData || {};
        setFormData(localFormData);
        setLocalVersion(parsed.localVersion || 1);
        setSyncStatus('DRAFT');
        lastAutoSavedJsonRef.current = JSON.stringify(localFormData);
        setAutoSaveStatus('saved');
        setAlert({ type: 'info', message: 'Loaded draft from local storage.' });
      } catch (e) {
        console.warn('Failed to parse local draft', e);
      }
    }
  }, []);


  const saveToLocal = useCallback((pId, sKey, docCode, data, version) => {
    const key = `${DRAFT_KEY_PREFIX}${pId}_${sKey}_${docCode}`;
    localStorage.setItem(key, JSON.stringify({
      formData: data, localVersion: version, savedAt: new Date().toISOString(),
      projectId: pId, scopeKey: sKey, documentCode: docCode,
    }));
    setLastSaved(new Date());
  }, []);

  const saveDraft = useCallback(async ({ silent = false } = {}) => {
    if (!projectId || !scopeKey || !documentCode) {
      if (!silent) {
        setAlert({ type: 'danger', message: 'Project ID, scope key, and document code are required.' });
      }
      return;
    }

    const formDataJson = JSON.stringify(formData || {});
    if (silent && formDataJson === lastAutoSavedJsonRef.current) {
      setAutoSaveStatus('saved');
      return;
    }

    if (silent) {
      setAutoSaveStatus('saving');
    } else {
      setSaving(true);
    }

    const nextVersion = localVersion + 1;
    if (isOffline) {
      saveToLocal(projectId, scopeKey, documentCode, formData, nextVersion);
      queueDraftForSync(projectId, scopeKey, documentCode, formData, nextVersion);
      setLocalVersion(nextVersion);
      setPendingSync(true);
      lastAutoSavedJsonRef.current = formDataJson;
      setAutoSaveStatus('saved');
      if (!silent) {
        setAlert({ type: 'warning', message: 'Saved locally. Will sync when online.' });
      }
    } else {
      try {
        const res = await api.post('/audit/form-drafts', {
          projectId: parseInt(projectId),
          scopeKey,
          documentCode,
          formData,
          createdBy: 'current_user',
          localVersion: nextVersion,
        });
        if (res.data?.success) {
          setDraftId(res.data.id);
          setSyncStatus(res.data.syncStatus || 'DRAFT');
          setLocalVersion(res.data.localVersion || nextVersion);
          setLastSaved(new Date());
          lastAutoSavedJsonRef.current = formDataJson;
          setAutoSaveStatus('saved');
          if (res.data.conflictData && Object.keys(res.data.conflictData).length > 0) {
            if (!silent) {
              setAlert({ type: 'warning', message: 'Conflict detected. Server version saved. Please review.' });
            }
            setSyncStatus('CONFLICT');
          } else {
            if (!silent) {
              setAlert({ type: 'success', message: 'Draft saved successfully.' });
            }
          }
        }
      } catch (error) {
        saveToLocal(projectId, scopeKey, documentCode, formData, nextVersion);
        queueDraftForSync(projectId, scopeKey, documentCode, formData, nextVersion, {
          status: 'FAILED',
          retryCount: 1,
          lastError: error?.response?.data?.message || error.message || 'Server save failed.',
        });
        setLocalVersion(nextVersion);
        setPendingSync(true);
        lastAutoSavedJsonRef.current = formDataJson;
        setAutoSaveStatus(silent ? 'error' : 'saved');
        if (!silent) {
          setAlert({ type: 'warning', message: 'Server save failed. Saved locally.' });
        }
      }
    }
    if (!silent) {
      setSaving(false);
    }
  }, [projectId, scopeKey, documentCode, formData, localVersion, isOffline, saveToLocal, queueDraftForSync]);

  const handleSave = useCallback(() => saveDraft({ silent: false }), [saveDraft]);

  useEffect(() => {
    if (!autoSaveEnabled) {
      return undefined;
    }
    if (!projectId || !scopeKey || !documentCode) {
      return undefined;
    }

    const formDataJson = JSON.stringify(formData || {});
    if (formDataJson === lastAutoSavedJsonRef.current) {
      return undefined;
    }

    setAutoSaveStatus('dirty');
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      saveDraft({ silent: true });
    }, 2000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [autoSaveEnabled, formData, projectId, scopeKey, documentCode, saveDraft]);

  const syncQueueItem = useCallback(async (item) => {
    const currentQueue = updateQueueItemStatus(readQueue(), item.id, { status: 'SYNCING', lastError: '' });
    persistQueue(currentQueue);

    try {
      const res = await api.post('/audit/form-drafts', {
        projectId: item.projectId,
        scopeKey: item.scopeKey,
        documentCode: item.documentCode,
        formData: item.formData,
        createdBy: 'current_user',
        localVersion: item.localVersion,
      });

      if (hasConflict(res.data)) {
        persistQueue(updateQueueItemStatus(readQueue(), item.id, {
          status: 'CONFLICT',
          conflictData: res.data.conflictData,
          retryCount: item.retryCount,
          lastError: 'Server has a newer version. Review conflict before removing queue item.',
        }));
        return false;
      }

      persistQueue(removeQueueItem(readQueue(), item.id));
      if (
        String(item.projectId) === String(projectId) &&
        item.scopeKey === scopeKey &&
        item.documentCode === documentCode
      ) {
        setDraftId(res.data?.id || draftId);
        setSyncStatus(res.data?.syncStatus || 'DRAFT');
        setLocalVersion(res.data?.localVersion || item.localVersion);
        setLastSaved(new Date());
      }
      return true;
    } catch (error) {
      persistQueue(updateQueueItemStatus(readQueue(), item.id, {
        status: 'FAILED',
        retryCount: (item.retryCount || 0) + 1,
        lastError: error?.response?.data?.message || error.message || 'Sync failed.',
      }));
      return false;
    }
  }, [documentCode, draftId, persistQueue, projectId, scopeKey]);

  const handleSyncAll = useCallback(async () => {
    if (isOffline) {
      setAlert({ type: 'warning', message: 'Cannot sync while offline.' });
      return;
    }
    const items = readQueue().filter((item) => item.status !== 'CONFLICT');
    if (items.length === 0) {
      setAlert({ type: 'info', message: 'No syncable queue items.' });
      return;
    }
    let successCount = 0;
    for (const item of items) {
      // Sequential sync keeps version conflicts deterministic.
      // eslint-disable-next-line no-await-in-loop
      if (await syncQueueItem(item)) successCount += 1;
    }
    setAlert({ type: successCount === items.length ? 'success' : 'warning', message: `${successCount}/${items.length} queued draft(s) synced.` });
  }, [isOffline, syncQueueItem]);

  const handleRemoveQueueItem = useCallback((itemId) => {
    persistQueue(removeQueueItem(readQueue(), itemId));
    setAlert({ type: 'info', message: 'Queue item removed.' });
  }, [persistQueue]);


  const handleSubmit = useCallback(async () => {
    if (!draftId) {
      setAlert({ type: 'danger', message: 'Save draft first before submitting.' });
      return;
    }
    if (!window.confirm('Submit this form? This will mark it as PUBLISHED.')) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/audit/form-drafts/${draftId}/submit`);
      if (res.data?.success) {
        setSyncStatus('PUBLISHED');
        setAlert({ type: 'success', message: 'Form submitted successfully.' });
      }
    } catch (error) {
      setAlert({ type: 'danger', message: error?.response?.data?.message || 'Submit failed.' });
    } finally {
      setSubmitting(false);
    }
  }, [draftId]);

  const handlePreview = useCallback(async () => {
    if (!projectId || !scopeKey || !documentCode) {
      setAlert({ type: 'danger', message: 'Project ID, scope key, and document code are required.' });
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/audit/form-drafts/project/${projectId}/scope/${scopeKey}/doc/${documentCode}/preview`);
      if (res.data?.found) {
        setFormData(res.data.formData || {});
        setSyncStatus(res.data.syncStatus || 'DRAFT');
        setAlert({ type: 'info', message: 'Preview loaded from server.' });
      } else {
        setAlert({ type: 'info', message: 'No form data to preview.' });
      }
    } catch (error) {
      setAlert({ type: 'danger', message: 'Failed to load preview.' });
    } finally {
      setLoading(false);
    }
  }, [projectId, scopeKey, documentCode]);

  const handleLoadCatalogItem = useCallback(async () => {
    if (!documentCode) return;
    setLoading(true);
    try {
      const res = await api.get('/audit/form-catalog', { params: { serviceLine: 'ISCC' } });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      const item = items.find(i => i.documentCode === documentCode);
      if (item) {
        setCatalogItem(item);
        setAlert({ type: 'info', message: `Loaded catalog item: ${item.documentName}` });
      } else {
        setAlert({ type: 'warning', message: 'Document code not found in catalog.' });
      }
    } catch (error) {
      setAlert({ type: 'danger', message: 'Failed to load catalog.' });
    } finally {
      setLoading(false);    }
  }, [documentCode]);

  const handleFormDataChange = useCallback((key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const renderFormFields = () => {
    if (!catalogItem) return (
      <div className="alert alert-info">Load a catalog item to see form fields, or enter document code and click Load.</div>
    );
    if (catalogItem.inputMode === 'upload_only') {
      return (
        <div className="alert alert-warning">
          This is an upload-only document. Use the evidence upload feature for this form type.
          <div className="mt-2">
            <a href="#" className="btn btn-sm btn-outline-primary" onClick={(e) => { e.preventDefault(); alert({ type: 'info', message: 'Evidence upload not implemented yet.' }); }}>
              Upload Evidence
            </a>
          </div>
        </div>
      );
    }
    if (catalogItem.inputMode === 'external_artifact') {
      return (
        <div className="alert alert-info">
          This is an external artifact reference. Document data is managed externally.
        </div>
      );
    }
    return (
      <div className="card">
        <div className="card-header"><h3 className="card-title">Form Data</h3></div>
        <div className="card-body">
          <p className="text-muted">Structured form entry for {catalogItem.documentCode}</p>
          <div className="mb-3">
            <label className="form-label">Auditor Name</label>
            <input type="text" className="form-control" value={formData.auditor_name || ''} onChange={(e) => handleFormDataChange('auditor_name', e.target.value)} placeholder="Enter auditor name" />
          </div>
          <div className="mb-3">
            <label className="form-label">Audit Date</label>
            <input type="date" className="form-control" value={formData.audit_date || ''} onChange={(e) => handleFormDataChange('audit_date', e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label">Audit Scope</label>
            <textarea className="form-control" rows={3} value={formData.audit_scope || ''} onChange={(e) => handleFormDataChange('audit_scope', e.target.value)} placeholder="Enter audit scope" />
          </div>
          {catalogItem.variantRules?.mandatory_fields?.includes('finding_summary') && (
            <div className="mb-3">
              <label className="form-label">Finding Summary <span className="text-danger">*</span></label>
              <textarea className="form-control" rows={4} value={formData.finding_summary || ''} onChange={(e) => handleFormDataChange('finding_summary', e.target.value)} placeholder="Enter finding summary" />
            </div>
          )}
          {catalogItem.variantRules?.mandatory_fields?.includes('ghg_calculations') && (
            <div className="mb-3">
              <label className="form-label">GHG Calculations <span className="text-danger">*</span></label>
              <textarea className="form-control" rows={4} value={formData.ghg_calculations || ''} onChange={(e) => handleFormDataChange('ghg_calculations', e.target.value)} placeholder="Enter GHG calculations" />
            </div>
          )}
          {catalogItem.variantRules?.mandatory_fields?.includes('corsia_eligibility_check') && (
            <div className="mb-3">
              <label className="form-label">CORSIA Eligibility Check <span className="text-danger">*</span></label>
              <textarea className="form-control" rows={4} value={formData.corsia_eligibility_check || ''} onChange={(e) => handleFormDataChange('corsia_eligibility_check', e.target.value)} placeholder="Enter CORSIA eligibility check" />
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Additional Notes</label>
            <textarea className="form-control" rows={3} value={formData.notes || ''} onChange={(e) => handleFormDataChange('notes', e.target.value)} placeholder="Additional notes" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-xl py-4">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title mb-1">Audit Form Entry</h2>
            <div className="text-muted">Structured ISCC audit form entry with draft-save and offline support.</div>
          </div>
          <div className="col-auto d-flex gap-2">
            {isOffline && <span className="badge bg-danger"><WifiOff size={14} className="me-1" /> Offline</span>}
            {pendingSync && <span className="badge bg-warning"><RefreshCcw size={14} className="me-1" /> Pending Sync</span>}
            {autoSaveEnabled && autoSaveStatus === 'saving' && <span className="badge bg-info">Menyimpan...</span>}
            {autoSaveEnabled && autoSaveStatus === 'saved' && <span className="badge bg-success">Semua perubahan disimpan</span>}
            {autoSaveEnabled && autoSaveStatus === 'dirty' && <span className="badge bg-secondary">Perubahan belum disimpan</span>}
            {autoSaveEnabled && autoSaveStatus === 'error' && <span className="badge bg-warning">Tersimpan lokal, menunggu sinkronisasi</span>}
            {lastSaved && <span className="text-muted small">Saved: {lastSaved.toLocaleTimeString()}</span>}
          </div>
        </div>
      </div>

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="card mb-3">
            <div className="card-header"><h3 className="card-title">Form Context</h3></div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Project ID</label>
                <input type="number" className="form-control" value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="Enter project ID" />
              </div>
              <div className="mb-3">
                <label className="form-label">Scope Key</label>
                <input type="text" className="form-control" value={scopeKey} onChange={(e) => setScopeKey(e.target.value)} placeholder="e.g. site-001" />
              </div>
              <div className="mb-3">
                <label className="form-label">Document Code</label>
                <div className="input-group">
                  <input type="text" className="form-control" value={documentCode} onChange={(e) => setDocumentCode(e.target.value)} placeholder="e.g. CBQA-ISCC-FRM-001" />
                  <button type="button" className="btn btn-outline-secondary" onClick={handleLoadCatalogItem}>Load</button>
                </div>
              </div>
              <div className="mb-3">
                <span className="badge bg-secondary">Status: {syncStatus}</span>
                <span className="badge bg-secondary ms-2">v{localVersion}</span>
              </div>
              <button type="button" className="btn btn-outline-primary w-100 mb-2" onClick={() => { loadDraftFromServer(projectId, scopeKey, documentCode); loadDraftFromLocal(projectId, scopeKey, documentCode); }} disabled={!projectId || !scopeKey || !documentCode || loading}>
                Load Draft
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3 className="card-title">Actions</h3></div>
            <div className="card-body">
              <button type="button" className="btn btn-primary w-100 mb-2" onClick={handleSave} disabled={saving || !projectId || !scopeKey || !documentCode}>
                <Save size={16} className="me-1" /> {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button type="button" className="btn btn-success w-100 mb-2" onClick={handleSubmit} disabled={submitting || !draftId || syncStatus === 'PUBLISHED'}>
                <Send size={16} className="me-1" /> {submitting ? 'Submitting...' : 'Submit'}
              </button>
              <button type="button" className="btn btn-outline-secondary w-100 mb-2" onClick={handlePreview} disabled={!projectId || !scopeKey || !documentCode}>
                <Eye size={16} className="me-1" /> Preview
              </button>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h3 className="card-title mb-0">Offline Sync Queue</h3>
              <span className="badge bg-secondary">{queueSummary.total}</span>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge bg-warning">Pending {queueSummary.pending}</span>
                <span className="badge bg-info">Syncing {queueSummary.syncing}</span>
                <span className="badge bg-danger">Failed {queueSummary.failed}</span>
                <span className="badge bg-dark">Conflict {queueSummary.conflict}</span>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary w-100 mb-3"
                onClick={handleSyncAll}
                disabled={isOffline || queueSummary.total === 0}
              >
                <RefreshCcw size={16} className="me-1" /> Sync Queue
              </button>
              {syncQueue.length === 0 ? (
                <div className="text-muted small">No local drafts waiting for sync.</div>
              ) : (
                <div className="list-group list-group-flush">
                  {syncQueue.map((item) => (
                    <div className="list-group-item px-0" key={item.id}>
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <div>
                          <strong className="d-block">{item.documentCode}</strong>
                          <span className="text-muted small">Project {item.projectId} / {item.scopeKey} / v{item.localVersion}</span>
                          <div className="small mt-1">
                            <span className={`badge ${item.status === 'CONFLICT' ? 'bg-dark' : item.status === 'FAILED' ? 'bg-danger' : 'bg-warning'}`}>{item.status}</span>
                            <span className="text-muted ms-2">Retry {item.retryCount || 0}</span>
                          </div>
                          {item.lastError ? <div className="text-danger small mt-1"><AlertCircle size={13} className="me-1" />{item.lastError}</div> : null}
                        </div>
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            disabled={isOffline || item.status === 'SYNCING'}
                            onClick={() => syncQueueItem(item)}
                            title="Retry sync"
                          >
                            <RefreshCcw size={14} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleRemoveQueueItem(item.id)}
                            title="Remove queue item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-8">
          {loading ? <Loading /> : renderFormFields()}
        </div>
      </div>
    </div>
  );
};

export default AuditFormEntry;
